import {FormControl, FormField} from 'platform/components';
import {Grid, GridItem} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';

import {UseFormReturn} from 'react-hook-form';

import {indexBy} from 'ramda';
import {isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {useTenant, useVatRatesOptions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {roundToTwoDecimals} from '../../RentalOrderHeader/utils/roundToTwoDecimals';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';

interface ChargedPriceRowProps extends RequiredTestIdProps {
  control: FormControl<ReturnVehicleFormValues>;
  formApi: UseFormReturn<ReturnVehicleFormValues>;
}

export function ChargedKilometersPriceRow(props: ChargedPriceRowProps) {
  const [vatRatesOptions] = useVatRatesOptions();
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const {tenantCurrency} = useTenant();
  const vatRatesByCode = indexBy((vatRate) => vatRate.value, vatRatesOptions);

  const handleRecalculateAfterVatRateChange = (value: string | number | null) => {
    if (!isString(value)) {
      return;
    }

    const chargedKilometer = props.formApi.getValues('rentPrice.chargedKilometer.price');
    const absoluteVatRate = vatRatesByCode?.[value ?? 'S']?.rate;
    const priceWithoutVat = chargedKilometer?.withoutVat ?? 0;
    const rate = parseFloat(absoluteVatRate ?? '0');

    const newPriceWithVat = roundToTwoDecimals(priceWithoutVat * (1 + rate / 100));
    const newVatPrice = roundToTwoDecimals(newPriceWithVat - priceWithoutVat);

    props.formApi.setValue(`rentPrice.chargedKilometer.price.withVat`, newPriceWithVat);
    props.formApi.setValue(`rentPrice.chargedKilometer.price.vat`, newVatPrice);
  };

  return (
    <Grid columns={4}>
      <Grid columns={4} spacing={2}>
        <FormField
          control={props.control}
          type="number"
          name="rentPrice.chargedKilometer.amount"
          label={i18n.t('page.rentalOrder.labels.chargedKilometer')}
          suffix={i18n.t('general.metric.km')}
          isDisabled
          data-testid={suffixTestId('amount', props)}
        />

        <GridItem span={3}>
          <FormField
            control={props.control}
            type="currency"
            name="rentPrice.chargedKilometer.price.withoutVat"
            label={i18n.t('page.rentalOrder.labels.chargedKilometerWithoutVat')}
            currency={tenantCurrency}
            decimalPlaces={2}
            isDisabled
            data-testid={suffixTestId(`priceWithoutVat`, props)}
          />
        </GridItem>
      </Grid>

      <FormField
        control={props.control}
        type="currency"
        name="rentPrice.chargedKilometer.price.withVat"
        label={i18n.t('page.rentalOrder.labels.chargedKilometerWithVat')}
        currency={tenantCurrency}
        decimalPlaces={2}
        isDisabled
        data-testid={suffixTestId(`priceWithVat`, props)}
      />

      <FormField
        control={props.control}
        type="currency"
        name="rentPrice.chargedKilometer.price.vat"
        decimalPlaces={2}
        label={`${i18n.t('entity.vehicle.labels.vat')} ${formatCurrencySymbol(tenantCurrency)}`}
        currency={tenantCurrency}
        isDisabled
        data-testid={suffixTestId(`priceVat`, props)}
      />

      <FormField
        control={props.control}
        type="choice"
        name="rentPrice.chargedKilometer.price.vatType"
        label={i18n.t('entity.vehicle.labels.vat')}
        options={vatRatesOptions}
        isNotClearable
        onChange={(value) => handleRecalculateAfterVatRateChange(value)}
        data-testid={suffixTestId(`priceVatRate`, props)}
      />
    </Grid>
  );
}
